import * as React from 'react';
import { useForm, Controller } from 'react-hook-form/dist/index.ie11';
import { useMutation } from '@apollo/react-hooks';

import TextField from 'crehana-ui/TextField';
import PhoneInput, { usePhoneNumberState } from 'Shared/PhoneInput';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';

import { useTranslation } from 'Jsx/Shared/Hooks/useTranslation';
import { LOGIN_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import getNextUrl from 'Jsx/Utils/getNextUrl';

import UPDATE_USER_MUTATION from '../graphql/updateUserData.graphql';

import LegalCheckbox, {
  LegalErrorMessage,
  rulesForRHFControllerComponent,
} from './LegalCheckbox';
import useContactData from '../hooks/useContactData';

const validation = {
  required: { value: true, message: 'Campo requerido' },
  maxLength: { value: 100, message: 'Máximo 100 caracteres' },
};

interface BasicDataFormProps {
  redirectUrl?: string;
  user?: {
    firstName: string;
    lastName: string;
    phoneCode: string;
    phoneNumber: string;
  } | null;
  withInnerPadding?: boolean;
  hideLegalInfo?: boolean;
  phoneNumberOptions?: {
    isRequired: boolean;
  };
  onlyPhoneNumber?: boolean;
  onSuccess?: () => void;
}

const BasicDataForm: React.FC<BasicDataFormProps> = ({
  redirectUrl,
  user,
  withInnerPadding,
  hideLegalInfo = false,
  onlyPhoneNumber = false,
  phoneNumberOptions = {
    isRequired: false,
  },
  onSuccess,
}) => {
  const { t } = useTranslation(LOGIN_TRANSLATION_KEY);
  const { saveContactData } = useContactData();
  const { phoneState, setPhoneState } = usePhoneNumberState();
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const {
    handleSubmit,
    register,
    errors,
    formState,
    getValues,
    control,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      checkboxLegal: true,
    },
  });

  React.useEffect(() => {
    mixpanel.track(mixpanel.EVENTS.RENDER_COMPONENT, {
      component: 'Auth - Extra data form',
      user: {
        hasPhoneNumber: !!user?.phoneNumber,
      },
    });
  }, []);

  const nextUrl = redirectUrl || getNextUrl();

  const onSubmit = async () => {
    const { firstName, lastName } = getValues();

    mixpanel.track(mixpanel.EVENTS.CLICK, {
      target: 'BasicDataForm - Primary button',
    });

    if (phoneState.phoneNumber.length > 0) {
      const MAX_ORIGIN_URL_LENGTH = 200;

      const saveContactDataResponse = await saveContactData({
        name: `${firstName || ''} ${lastName || ''}`,
        phoneNumber: phoneState.fullNumber,
        originUrl: window.location.href.substring(0, MAX_ORIGIN_URL_LENGTH),
      });

      if (!saveContactDataResponse.success) {
        throw new Error('Error saving phone number');
      }
    }

    const variables = onlyPhoneNumber
      ? {
          phoneNumber: phoneState.phoneNumber,
          code: phoneState.code,
        }
      : {
          firstName,
          lastName,
          phoneNumber: phoneState.phoneNumber,
          code: phoneState.code,
        };
    const updateUserResponse = await updateUser({
      variables,
    });

    if (updateUserResponse.data?.updateUser.success) {
      mixpanel.track(mixpanel.EVENTS.REGISTER_EXTRA_DATA, {
        hasFirstName: firstName && firstName.length > 0,
        hasLastName: lastName && lastName.length > 0,
        hasPhoneNumber: phoneState.phoneNumber.length > 0,
        destinationUrl: nextUrl,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        window.location.href = nextUrl;
      }
    } else {
      throw new Error('Error saving user data');
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h4 className="mb-12 text-center font-h2">
        {t('AUTH_EXTRA_DATA_FORM_TITLE', {
          defaultValue: 'Llévanos contigo',
        })}
      </h4>
      <div className={withInnerPadding ? 'sm:px-56 md:px-64 lg:px-96' : ''}>
        <p className="text-center font-body1">
          {t('AUTH_EXTRA_DATA_FORM_SUBTITLE', {
            defaultValue:
              'Esta información servirá para darle seguimiento al progreso de tus cursos, y conocer tu experiencia en la plataforma',
          })}
        </p>
        <div className="flex flex-col items-center w-full mt-40 mx-auto">
          <div className="w-full mb-24 sm:mb-32" style={{ maxWidth: 384 }}>
            {!onlyPhoneNumber && (
              <React.Fragment>
                <div className="mb-24 w-full">
                  <TextField
                    wrapperClassName="bg-white"
                    maxLength={100}
                    name="firstName"
                    defaultValue={user?.firstName || ''}
                    label={t('NAMES', 'Nombres')}
                    className="w-full"
                    ref={register(validation)}
                    errorText={errors.firstName?.message}
                  />
                </div>
                <div className="mb-24">
                  <TextField
                    wrapperClassName="bg-white"
                    maxLength={100}
                    name="lastName"
                    defaultValue={user?.lastName || ''}
                    type="text"
                    label={t('LAST_NAMES', 'Apellidos')}
                    className="w-full"
                    ref={register(validation)}
                    errorText={errors.lastName?.message}
                  />
                </div>
              </React.Fragment>
            )}
            <div className="mb-8 w-full">
              {onlyPhoneNumber && (
                <p className="font-subtitle2 font-bold mb-8">Número Celular</p>
              )}
              <PhoneInput
                showAll
                onChange={setPhoneState}
                isRequired={phoneNumberOptions.isRequired}
                initialState={
                  user?.phoneNumber
                    ? {
                        code: user.phoneCode,
                        phoneNumber: user.phoneNumber,
                      }
                    : undefined
                }
                label="Número de celular"
              />
              <p className="font-body3 text-base-lighter mt-8">
                {t('DONT_WORRY_WE_DONT_SHARE_YOUR_DATA', {
                  defaultValue: 'Descuida, no lo compartiremos con nadie.',
                })}
              </p>
            </div>
            {!hideLegalInfo && (
              <div className="mb-24 w-full">
                <Controller
                  control={control}
                  name="checkboxLegal"
                  render={({ onChange, onBlur, value }) => (
                    <LegalCheckbox
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                    />
                  )}
                  rules={rulesForRHFControllerComponent(t)}
                />

                {errors.checkboxLegal && (
                  <LegalErrorMessage message={errors.checkboxLegal.message} />
                )}
              </div>
            )}
          </div>
          <PrimaryButton
            size="l"
            label={t('START', {
              defaultValue: 'Empezar',
            })}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || !phoneState.isValid}
          />
        </div>
      </div>
    </div>
  );
};

export default BasicDataForm;
